import { getLocale } from '@sonnen/web-utils';
import { JSX, useMemo } from 'react';
import {
  allRoutes,
  AppRoute,
  EMPTY_DELIVERY_ADDRESS,
  modulePropertyByName,
  ROUTE_AFTER_LOGIN_KEY,
} from '../constants';
import {
  CurrentUserAccountBillingAddress,
  CurrentUserContact,
  CurrentUserSite,
  CurrentUserSiteOptionsCreateContractFlow,
  CustomerSalutation,
  CustomerUserAccountType,
  EnergyDynamicProductConfigurationWithBatteryOptimizationModule,
  EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule,
  EnergyFixProductConfigurationWithFeedInModule,
  EnergyFixProductConfigurationWithoutFeedInModule,
  ProductConfigurations,
  TerminationPeriod,
} from '../graphql/generated';
import { useTranslation } from '../i18n/i18n';
import {
  ContractCreationBlockedReason,
  CurrentUser,
  DayAheadPriceConfiguration,
  ModuleName,
  OfferErrorTranslationKey,
  OrderFlow,
  SelectOptionType,
  TAddress,
  TariffConfiguration,
  TCompleteAddress,
  TDeliveryDetails,
  TFormValues,
} from '../types';
import { formatCurrency } from './formatting';
import { getCurrencyPropertyValue, getTwoDigitsString } from './formatting';

const {
  NewContract,
  SwitchFromFlatXContract,
  SwitchFromSonnenStromContract,
  SwitchFromFlatBindingContract,
} = CurrentUserSiteOptionsCreateContractFlow;
const { BlockedDueToContract, BlockedDueToFlatXContract } =
  CurrentUserSiteOptionsCreateContractFlow;
const { sonnenHome, notAvailable } = ContractCreationBlockedReason;
const SITE_ID_QUERY_PARAM = 'siteId';
const SITE_ID_STORAGE_KEY = 'selected-site-id-from-url';

/**
 * @param date the date to which to add the given number of days.
 * @param days the number of days to add to the given date. Default is 20 days.
 * @returns a new date object with the given number of days added.
 */
export const inDays = (days: number, date: Date = new Date()): Date => {
  const zeroedDate = new Date(date.toISOString().split('T')[0]);
  return new Date(zeroedDate.getTime() + days * 24 * 60 * 60 * 1000);
};

export const in20Days = (date = new Date()): Date => inDays(20, date);

export const addWorkingDays = (days: number, date: Date = new Date()): Date => {
  const result = new Date(date.toISOString().split('T')[0]);
  let remaining = days;
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) remaining--;
  }
  return result;
};

export const getActualDeliveryStartDate = (
  values: Pick<
    TDeliveryDetails,
    | 'confirmedCancellationDate'
    | 'contractTerminated'
    | 'deliveryStartDate'
    | 'providerChangeReason'
  >,
): Date => {
  const { contractTerminated, confirmedCancellationDate, deliveryStartDate } = values;
  const minDate = addWorkingDays(4);
  const minDateTime = minDate.getTime();

  if (contractTerminated) {
    if (!confirmedCancellationDate) return minDate;
    if (confirmedCancellationDate.getTime() < minDateTime) return minDate;
    const returnDate = new Date(confirmedCancellationDate.getTime() + 24 * 60 * 60 * 1000);
    return returnDate;
  }

  if (!deliveryStartDate || deliveryStartDate.getTime() < minDateTime) {
    return minDate;
  }
  return deliveryStartDate;
};

export const localeDateString = (date: Date) =>
  date.toLocaleDateString(navigator.language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

/**
 * Generates an array of month options with translated names
 * @param t The translation function from useTranslation()
 * @param year Optional year string. When '2025', February is split into two options (before/after the 26th).
 * @returns Array of month options with value (1-12) and translated label
 */

export const getDefaultLocale = () => {
  const url = new URL(window.location.href);
  const lang = url.searchParams.get('lang');
  if (lang === 'de') return 'de';
  if (lang?.startsWith('en')) return 'en-GB';

  return getLocale() === 'de' ? 'de' : 'en-GB';
};
export const getMonthOptions = (selectedYear?: string) => {
  const locale = getDefaultLocale();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-based
  const isCurrentYear = selectedYear ? Number(selectedYear) === currentYear : false;

  return Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString(),
    label: new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2024, i, 1)),
  })).filter((m) => !isCurrentYear || Number(m.value) < currentMonth);
};

/**
 * Formats a date string or Date object to a localized month name
 * @param dateString Date string or Date object
 * @param format 'short' for abbreviated month (Jan) or 'long' for full month name (January)
 * @returns Localized month name
 */
export const formatMonth = (dateString: string | Date, format: 'short' | 'long'): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return date.toLocaleString(getDefaultLocale(), { month: format });
};

/**
 * Extracts the year from a date string
 * @param dateString Date string in ISO format
 * @returns Year as number
 */
export const formatYear = (dateString: string): number => new Date(dateString).getFullYear();

export const getRedirectUri = () => {
  const ticketNumber = window.location.pathname.match(/SON-\d+/)?.[0] || '';

  const url = new URL(window.location.origin);
  url.pathname = `${ticketNumber}/login`;
  return url.toString();
};

export const getLogoutRedirectUri = () => {
  const env = process.env.VITE_ENV;
  let environment = '';
  if (env === 'staging') environment = 'staging.';
  if (env === 'development') environment = 'dev.';
  return `https://tariffs.${environment}sonnen.de`;
};

export const getFullName = (contact: Pick<CurrentUserContact, 'firstName' | 'lastName'>) => {
  const { firstName = '', lastName = '' } = contact || {};
  return [firstName, lastName].filter(Boolean).join(' ');
};

/**
 * @param date Date object.
 * @returns a string with the format yyyy-mm-dd or empty string.
 */
export const formatDateYYYYMMDD = (date?: Date) => {
  if (!date || Object.prototype.toString.call(date) !== '[object Date]') return '';

  return date.toISOString().split('T')[0];
};

/**
 * Returns either selected site address, the user's billing address or an empty value object.
 * @param user
 * @param selectedSite
 * @returns a delivery address object.
 */
export const getValidDeliveryAddress = (
  user: CurrentUser | null,
  selectedSite?: CurrentUserSite | null,
): TAddress => {
  const target = selectedSite?.address || user?.account?.billingAddress || EMPTY_DELIVERY_ADDRESS;

  return {
    city: target.city || '',
    country: target.country || '',
    postalCode: target.postalCode || '',
    streetName: target.streetName || '',
    streetNumber: target.streetNumber || '',
  };
};

/**
 * @param value number
 * @param precision number
 * @returns a rounded up or down number with decimals according to the precision passed
 */
export function roundWithPrecision(value: number, precision?: number): number {
  const multiplier = 10 ** (precision ?? 0);
  return Math.round(value * multiplier) / multiplier;
}

export const siteToOptionType = (
  site: CurrentUserSite,
  batteryLabel?: string,
  contractLabel?: string,
): SelectOptionType => {
  const serialNumber = site.batteries?.[0]?.serialNumber;
  const serialNumberString = serialNumber ? `${serialNumber} - ` : '';
  const battery = site.options.hasBatteries ? batteryLabel : '';
  const contract = site.options.createContractFlow === NewContract ? '' : contractLabel;
  const additionalText = battery && contract ? `${battery} & ${contract}` : battery || contract;

  return {
    label: `${serialNumberString}${site.address.streetName} ${site.address.streetNumber}, ${site.address.postalCode} ${site.address.city}`,
    value: site.id,
    additionalText,
  };
};

export const stringToOptionType = (value: string): SelectOptionType => ({ label: value, value });

/**
 * Returns a string or an empty string if the value is null.
 * @param value
 */
export const nullOrString = (value: string | null): string => value ?? '';

/**
 * Removes null values from an array
 * @param values
 */
export const getNonNullValues = (
  values: (string | null)[] | never[] | null | undefined,
): string[] => {
  const okValues = values?.filter((value) => value !== null) || [];
  return okValues.map(nullOrString);
};

/**
 * Returns the corresponding salutation object
 * @param salutationOptions array of salutations with values and translated labels
 * @param checkValue value to be found
 */
export const getSalutation = (
  salutationOptions: SelectOptionType[],
  checkValue: string,
): SelectOptionType => {
  return salutationOptions.filter((option: SelectOptionType) => option.value === checkValue)[0];
};

/**
 * Provides translations for the salutation dropdown
 */
export const useSalutations = () => {
  const { t } = useTranslation();
  return [
    { label: t('data.personalInfo.salutationOptions.woman'), value: CustomerSalutation.Ms },
    { label: t('data.personalInfo.salutationOptions.man'), value: CustomerSalutation.Mr },
  ];
};

/**
 * Removes the leading zero if string starts with it
 * @param string
 */
export const removeLeadingZero = (input: string): string =>
  input.startsWith('0') ? input.slice(1) : input;

/**
 * Allows only one empty space by removing all the next ones
 * @param string
 */
export const removeEmptySpaces = (value: string) => value.replace(/\s+/g, ' ');

/*
 * Retrieves grid purchase value or returns 0 if site does not have grid purchase
 * @param site
 */
export const getGridPurchase = (site?: CurrentUserSite | null) =>
  site?.previousYearGridPurchase?.aggregation
    ? roundWithPrecision(site?.previousYearGridPurchase?.aggregation)
    : 0;

/**
 * Retrieves battery id from site object
 * @param site
 */
export const getBatteryId = (site: CurrentUserSite | null) => site?.batteries?.[0]?.id;

/**
 * Returns true if the selected site is allowed to create a contract.
 * @param site
 */
export const getContractCreationAllowed = (site: CurrentUserSite | null) => {
  if (!site?.options.createContractFlow) return false;
  return [
    NewContract,
    SwitchFromFlatBindingContract,
    SwitchFromFlatXContract,
    SwitchFromSonnenStromContract,
  ].includes(site.options.createContractFlow);
};

export const hasSonnenStromContract = (site: CurrentUserSite | null) =>
  site?.options.createContractFlow === SwitchFromSonnenStromContract;

/**
 * Gets the default site to be displayed as value in the sites dropdown, following this prioritization:
 * 1. site with battery, no contract (NEW_CONTRACT) and grid purchase
 * 2. site with battery, no contract (NEW_CONTRACT) and no grid purchase
 * 3. site with no battery and contract allowed to switch (SWITCH_FROM_FLAT_X_CONTRACT)
 * 4. if none apply, it shows the first site in the array, which will trigger the modal
 * Returns null if there is no user or no sites
 * @param user
 */
export const getDefaultSite = (user: CurrentUser | null): CurrentUserSite | null => {
  if (!user?.sites?.length) return null;

  const sitesWithBatteries = user.sites.filter((site) => site.options.hasBatteries);
  const siteNewContractWithGridPurchase = sitesWithBatteries.find(
    (site) => site.options.createContractFlow === NewContract && site.previousYearGridPurchase,
  );
  if (siteNewContractWithGridPurchase) return siteNewContractWithGridPurchase;

  const siteNewContract = sitesWithBatteries.find(
    (site) => site.options.createContractFlow === NewContract,
  );
  if (siteNewContract) return siteNewContract;

  return user.sites.find(getContractCreationAllowed) || user.sites[0];
};

const getSiteIdFromSearch = (search: string): string | null => {
  return new URLSearchParams(search).get(SITE_ID_QUERY_PARAM);
};

const persistSiteId = (siteId: string) => {
  sessionStorage.setItem(SITE_ID_STORAGE_KEY, siteId);
};

const getPersistedSiteId = (): string | null => {
  return sessionStorage.getItem(SITE_ID_STORAGE_KEY);
};

/**
 * Persists siteId from the current query and, if missing, enriches the query with the persisted siteId.
 * Returns a full path containing merged query params.
 */
export const buildPathWithPersistedSiteId = (
  path: AppRoute,
  search: string,
): `${AppRoute}${string}` => {
  const params = new URLSearchParams(search);
  const siteIdFromQuery = getSiteIdFromSearch(search);

  if (siteIdFromQuery) {
    persistSiteId(siteIdFromQuery);
  }

  const siteId = siteIdFromQuery || getPersistedSiteId();

  if (siteId && !params.get(SITE_ID_QUERY_PARAM)) {
    params.set(SITE_ID_QUERY_PARAM, siteId);
  }

  const nextSearch = params.toString();
  return `${path}${nextSearch ? `?${nextSearch}` : ''}` as `${AppRoute}${string}`;
};

/**
 * Returns the site selected through URL query params (UUID from mobile app).
 * The mobile app passes the site ID via the `siteId` query parameter.
 *
 * @example
 * // Mobile app deep link with UUID site ID
 * https://example.com/?siteId=b335c94c-9f42-4997-b4b6-e795b6526b68
 */
export const getSelectedSiteFromUrl = (user: CurrentUser | null): CurrentUserSite | null => {
  if (!user?.sites?.length) return null;

  const url = new URL(window.location.href);
  const siteIdFromQuery = url.searchParams.get(SITE_ID_QUERY_PARAM);

  if (siteIdFromQuery) {
    persistSiteId(siteIdFromQuery);
  }

  const siteId = siteIdFromQuery || getPersistedSiteId();

  if (!siteId) return null;

  const selectedSite = user.sites.find((site) => site.id === siteId);
  return selectedSite || null;
};

/**
 * Returns address without values of type null, undefined or Maybe<>
 */
export const completeAddress = (
  address: TAddress | CurrentUserAccountBillingAddress,
): TCompleteAddress => ({
  city: address.city || '',
  country: address.country || '',
  postalCode: address.postalCode || '',
  streetName: address.streetName || '',
  streetNumber: address.streetNumber || '',
});

export const getActualBillingAddress = (
  formValues: Pick<TFormValues, 'billingAddress' | 'deliveryAddress'>,
  useCustomBillingAddress: boolean,
): TCompleteAddress => {
  return completeAddress(
    formValues[useCustomBillingAddress ? 'billingAddress' : 'deliveryAddress'],
  );
};

export const camelToSnake = (key: string) => {
  const result = key.replace(/([A-Z])/g, ' $1');
  return result.split(' ').join('-').toLowerCase();
};

export const businessAccount = (account: CustomerUserAccountType) =>
  account === CustomerUserAccountType.BusinessCustomer;

// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
export const ExternalLinkComponent = ({ node, ...props }: any) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content, react/jsx-props-no-spreading
  <a {...props} target="_blank" rel="noopener noreferrer" />
);

export const moduleAvailable = (offer: ProductConfigurations | null, name: ModuleName) => {
  if (name === ModuleName.DIRECT_MARKETING) {
    return Boolean(offer?.energyFix?.configurationWithFeedInModule);
  }
  const propertyName = modulePropertyByName[name];
  if (!propertyName) return false;
  return Boolean(offer?.energyDynamic?.[propertyName]);
};

export const memoize = (children: JSX.Element) => useMemo(() => children, []);

export const getSwitchFromExistingContract = (site: CurrentUserSite | null) =>
  Boolean(
    site?.options?.createContractFlow === SwitchFromFlatXContract ||
      site?.options?.createContractFlow === SwitchFromSonnenStromContract ||
      site?.options?.createContractFlow === SwitchFromFlatBindingContract,
  );

/**
 * @returns one of tariffs configuration, or null.
 */
export const getSelectedTariffConfiguration = (
  offer: ProductConfigurations | null,
  modules: Set<ModuleName> = new Set(),
  orderFlow: OrderFlow | null,
):
  | EnergyDynamicProductConfigurationWithBatteryOptimizationModule
  | EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule
  | EnergyFixProductConfigurationWithFeedInModule
  | EnergyFixProductConfigurationWithoutFeedInModule
  | null
  | undefined => {
  if (orderFlow === OrderFlow.energyFix && offer?.energyFix) {
    const withFeedIn = offer.energyFix.configurationWithFeedInModule;
    const withoutFeedIn = offer.energyFix.configurationWithoutFeedInModule;
    return modules.has(ModuleName.DIRECT_MARKETING) && withFeedIn
      ? withFeedIn
      : withoutFeedIn || withFeedIn;
  }

  const withBattery = offer?.energyDynamic?.configurationWithBatteryOptimizationModule;
  const withoutBattery = offer?.energyDynamic?.configurationWithoutBatteryOptimizationModule;

  return modules.has(ModuleName.PRICE_OPTIMIZATION) && withBattery ? withBattery : withoutBattery;
};

export const getOfferErrorCode = (code?: unknown): OfferErrorTranslationKey => {
  if (typeof code === 'string' && code in OfferErrorTranslationKey) {
    return OfferErrorTranslationKey[code as keyof typeof OfferErrorTranslationKey];
  }
  return OfferErrorTranslationKey.SERVER;
};

export const getValidRoute = (pathname: string) => {
  return allRoutes.some((route) => route === pathname);
};

export const getRouteAfterLogin = (): string | null => {
  return localStorage.getItem(ROUTE_AFTER_LOGIN_KEY);
};

export const setRouteAfterLogin = (value?: `${AppRoute}${string}`): void => {
  if (!value) {
    localStorage.removeItem(ROUTE_AFTER_LOGIN_KEY);
    return;
  }
  localStorage.setItem(ROUTE_AFTER_LOGIN_KEY, value);
};

export const isEnergyDynamicProductConfiguration = (
  configuration?: TariffConfiguration | null,
): configuration is
  | EnergyDynamicProductConfigurationWithoutBatteryOptimizationModule
  | EnergyDynamicProductConfigurationWithBatteryOptimizationModule => {
  if (!configuration) return false;
  return Object.keys(configuration).some((key) => key === 'averagePrices');
};

export const getPriceAfterFirstYear = (
  offer: ProductConfigurations | null,
  modules: Set<ModuleName> = new Set(),
): string | undefined | null => {
  const { dayAheadPriceBenefitDetailsAmount } =
    offer?.energyDynamic?.configurationWithBatteryOptimizationModule?.batteryOptimizationModule ||
    {};
  return dayAheadPriceBenefitDetailsAmount && modules.has(ModuleName.PRICE_OPTIMIZATION)
    ? formatCurrency(dayAheadPriceBenefitDetailsAmount, 'euro', true)
    : null;
};

export const getDayAheadLabels = (configuration?: DayAheadPriceConfiguration | null) => {
  let firstYearPrice = 0;
  let priceAfterFirstYear = getTwoDigitsString('4,99');
  let terminationPeriodOption = TerminationPeriod.Termination_30DaysToEndOfMonth;

  if (configuration) {
    const {
      dayAheadPriceBaseFeeGrossPerMonth,
      dayAheadPriceBenefitDetailsAmount,
      dayAheadPriceTerminationPeriodOption,
    } = configuration;

    if (dayAheadPriceTerminationPeriodOption)
      terminationPeriodOption = dayAheadPriceTerminationPeriodOption;

    firstYearPrice =
      getCurrencyPropertyValue(dayAheadPriceBaseFeeGrossPerMonth) -
      getCurrencyPropertyValue(dayAheadPriceBenefitDetailsAmount);

    if (dayAheadPriceBenefitDetailsAmount)
      priceAfterFirstYear = getTwoDigitsString(dayAheadPriceBenefitDetailsAmount.value);
  }

  return {
    firstYearPrice,
    priceAfterFirstYear,
    terminationPeriodOption,
  };
};

export const getBatteryOptimizationBlocked = (offer?: ProductConfigurations | null) => {
  return Boolean(!offer?.batteryOptimization?.configuration);
};

export const getContractCreationBlocked = (
  selectedSite: CurrentUserSite | null,
  offer?: ProductConfigurations | null,
  orderFlow?: OrderFlow | null,
): ContractCreationBlockedReason | null => {
  const { hasConcludedSonnenHomeContract, createContractFlow } = selectedSite?.options || {};
  if (hasConcludedSonnenHomeContract) return sonnenHome;

  const blockedDueToContract =
    createContractFlow &&
    [BlockedDueToContract, BlockedDueToFlatXContract].includes(createContractFlow);

  const batteryOptimizationBlocked =
    offer && orderFlow === OrderFlow.batteryOptimization && getBatteryOptimizationBlocked(offer);

  return blockedDueToContract || batteryOptimizationBlocked ? notAvailable : null;
};
